// Helpers for the balance summary and the active-period timeline.

// balanceByCurrency merges accounts with a non-zero USD balance per currency, largest first.
export const balanceByCurrency = (accounts = []) => {
  const totals = (accounts || []).reduce(
    (acc, a) => (a.balance_usd_cents ? { ...acc, [a.currency]: (acc[a.currency] || 0) + a.balance_usd_cents } : acc),
    {},
  );
  return Object.entries(totals)
    .map(([currency, usdCents]) => ({ currency, usd_cents: usdCents }))
    .sort((x, y) => y.usd_cents - x.usd_cents || x.currency.localeCompare(y.currency));
};

// timelinePct places ts on the start→end range as a percentage, clamped to 0–100.
export const timelinePct = (ts, start, end) => {
  if (!ts || !start || !end) return null;
  const from = new Date(start).getTime();
  const span = new Date(end).getTime() - from;
  if (span <= 0) return 0;
  return Math.min(100, Math.max(0, ((new Date(ts).getTime() - from) / span) * 100));
};

const EVENT_KINDS = [['deposits', 'deposit'], ['bonuses', 'bonus'], ['cashouts', 'cashout']];

// weekOf returns the API's week, or a one-period week when there is none.
export const weekOf = (week, period) =>
  week || { start: period.period_start, end: period.period_end, periods: [period] };

const periodsOf = (range) => range.periods || [range];
const rangeStart = (range) => range.start || range.period_start;
const rangeEnd = (range) => range.end || range.period_end;

// timelineEvents merges the deposits, bonuses and cashouts of a week (or a single period) into one
// chronological list, each placed on the range and flagged when it belongs to the active period.
export const timelineEvents = (range) =>
  periodsOf(range)
    .flatMap((p) => EVENT_KINDS.flatMap(([key, kind]) => (p[key] || []).map((t) => ({
      ...t,
      kind,
      active: p.active !== false,
      pct: timelinePct(t.created_at, rangeStart(range), rangeEnd(range)),
    }))))
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

// bonusResetNote says why a bonus listed inside a period did not move the start point, from the
// table's is_reset_bonus_type: a bonus type that never resets, or a reset type the table kept
// inside the period. No badge when the table does not say.
export const bonusResetNote = (event) => {
  if (event.kind !== 'bonus') return null;
  if (event.is_reset_bonus_type === false) return { label: "Doesn't reset period", variant: 'outline' };
  if (event.is_reset_bonus_type === true) return { label: "Reset type · didn't reset here", variant: 'warning' };
  return null;
};

const MINUTE_MS = 60 * 1000;

// formatDuration renders the span between two timestamps as "1d 0h 41m" (days only when ≥ 1).
export const formatDuration = (from, to) => {
  if (!from || !to) return '—';
  const minutes = Math.max(0, Math.floor((new Date(to) - new Date(from)) / MINUTE_MS));
  const d = Math.floor(minutes / (24 * 60));
  const h = Math.floor((minutes % (24 * 60)) / 60);
  const m = minutes % 60;
  return d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m`;
};

// formatShortUTC renders a timestamp as "Wed 14 Oct 21:18" in UTC.
export const formatShortUTC = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  const day = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).replace(',', '');
  return `${day} ${d.toISOString().slice(11, 16)}`;
};

// Bonus statuses (vip_comp_periods bonuses[].status) → badge label + variant.
const BONUS_STATUS = {
  handle_bets: { label: 'Wagering', variant: 'info' },
  wager_done: { label: 'Wagered', variant: 'success' },
  lost: { label: 'Lost', variant: 'secondary' },
  canceled: { label: 'Canceled', variant: 'outline' },
  expired: { label: 'Expired', variant: 'secondary' },
  issued: { label: 'Issued, not activated', variant: 'warning' },
};

export const bonusStatusMeta = (status) =>
  (status ? BONUS_STATUS[status] || { label: status, variant: 'outline' } : null);

// Back office payment page; a deposit's action_id is its payment (deposit) ID. BitStarz only:
// bonus requests run on BitStarz data.
const BACKOFFICE_PAYMENT_URL = 'https://backoffice.bitstarz-kute.com/backend/payments';
// Back office bonus page; a bonus action's action_id is its bonus (issue) ID.
const BACKOFFICE_BONUS_URL = 'https://backoffice.bitstarz-kute.com/backend/bonus_issues';

const PAYMENT_KINDS = new Set(['deposit', 'cashout']);

// paymentBackofficeUrl links a deposit or cashout event to its back office payment (action_id is
// the payment ID); null for bonuses or an event without an ID.
export const paymentBackofficeUrl = (event) => {
  if (!PAYMENT_KINDS.has(event?.kind) || event.action_id === null || event.action_id === undefined) return null;
  return `${BACKOFFICE_PAYMENT_URL}/${encodeURIComponent(event.action_id)}`;
};

// bonusBackofficeUrl links a back office bonus (bonus_issues) by its ID; null without one.
export const bonusBackofficeUrl = (bonusId) => (bonusId === null || bonusId === undefined || bonusId === ''
  ? null
  : `${BACKOFFICE_BONUS_URL}/${encodeURIComponent(bonusId)}`);

const hasValue = (v) => v !== null && v !== undefined && v !== '';

// fridayDate reads a Friday reset's action_id (YYYYMMDD) as "Fri 16 Oct 2026"; null when it is
// not a valid date.
export const fridayDate = (actionId) => {
  const m = /^(\d{4})(\d{2})(\d{2})$/.exec(String(actionId ?? ''));
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (d.getUTCMonth() !== Number(m[2]) - 1 || d.getUTCDate() !== Number(m[3])) return null;
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).replace(',', '');
};

// resetActionView describes the action that opened the period — the start point — for the first
// row of the event list. action_id is the Friday's date (YYYYMMDD), the bonus ID or the cashout's
// payment ID; a cashout links to its back office payment, a bonus to its bonus issue.
export const resetActionView = (period) => {
  if (!period?.action) return null;
  const base = {
    kind: period.action,
    time: period.action_created_at || period.period_start,
    amount: hasValue(period.action_amount) ? period.action_amount : null,
    currency: period.action_currency || null,
    amountUsd: hasValue(period.action_amount_usd) ? period.action_amount_usd : null,
    id: hasValue(period.action_id) ? String(period.action_id) : null,
    href: null,
    activatedAt: period.action_activated_at || null,
    finishedAt: period.action_finished_at || null,
  };
  switch (period.action) {
    case 'friday':
      return {
        ...base, label: 'Weekly reset', detail: fridayDate(period.action_id) || 'Friday', id: null, amount: null, amountUsd: null,
        activatedAt: null, finishedAt: null,
      };
    case 'cashout':
      return {
        ...base, label: 'Cashout', detail: null,
        href: base.id ? `${BACKOFFICE_PAYMENT_URL}/${encodeURIComponent(base.id)}` : null,
      };
    case 'bonus':
      return {
        ...base, label: 'Bonus', detail: period.action_title || null, status: period.action_status || null,
        href: base.id ? bonusBackofficeUrl(base.id) : null,
      };
    default:
      return { ...base, label: period.action, detail: null };
  }
};
